<script>
import BaseIcon from '@/rex-components/BaseIcon.vue';
import RexButton from '@/rex-components/rex-button.vue';
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import RexFormInput from '@/rex-components/rex-modal-input.vue';
import RexModalV3 from '@/rex-components/RexModalV3.vue';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import RexLoading from '@/rex-components/rex-loading.vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import {
  CREATE_CONNECTION,
  CURRENT_API_STATUS,
  EDIT_CONNECTION,
} from '@/store/modules/cloud/connections/services.constants';
import { CloudService } from '@/services/api/integration.service';
import { notificationError } from '@/utils/notification';
import { isEqual } from 'lodash';
import RexCheckboxV3 from '@/rex-components/RexCheckboxV3.vue';

export default {
  components: {
    RexButton,
    BaseIcon,
    RexModalLabel,
    RexFormInput,
    RexInfoLabel,
    RexLoading,
    RexModalV3,
    RexCheckboxV3,
  },
  emits: ['onClose'],
  beforeMount: async function () {
    await this.getConfigurationInfoBySubscriptionId();
  },
  methods: {
    isEqual,
    ...mapActions('cloudState/connections', [
      CREATE_CONNECTION,
      EDIT_CONNECTION,
    ]),
    ...mapMutations('cloudState/connections', [CURRENT_API_STATUS]),
    resetCurrentApiStatus() {
      this[CURRENT_API_STATUS]('idle');
    },
    onWindowHide() {
      for (let key in this.userInputs) {
        this.userInputs[key] = '';
      }
      for (let key in this.errorState) {
        this.errorState[key] = false;
      }
      this.$emit('onClose');
    },
    handleSubmit() {
      const isUserInputValidated = this.validateUserInput();
      if (!isUserInputValidated) {
        return;
      }
      if (this.type === 'edit') {
        this[EDIT_CONNECTION]({
          ...this.userInputs,
          aliasEnabled: 1,
        });
      } else {
        this[CREATE_CONNECTION]({
          aliasEnabled: 1,
          accessKeyId: this.userInputs.accessKey,
          secretAccessKey: this.userInputs.accessSecret,
          pullFrequency: this.userInputs.pullInterval,
          connectionName: this.userInputs.connectionName,
        });
      }
    },
    validateUserInput() {
      let isValid = true;

      // Reset all error states
      for (let key in this.errorState) {
        this.errorState[key] = false;
      }

      // Validate each field in userInputs
      for (let key in this.userInputs) {
        const skipValidation = ['subscriptionId'];
        if (!skipValidation.includes(key) && !this.userInputs[key]) {
          this.errorState[key] = true; // Set error state to true for invalid fields
          isValid = false; // Set isValid to false if any field is invalid
        }
      }
      if (
        (this.userInputs.pullInterval && isNaN(this.userInputs.pullInterval)) ||
        this.userInputs.pullInterval?.includes?.('.')
      ) {
        this.errorState.pullInterval = this.$t(
          'aws.createNewConfigguration.errorState.inValidPullFrequency',
        );
        isValid = false;
      }
      if (!/^[a-zA-Z0-9\s-]+$/.test(this.userInputs.connectionName)) {
        this.errorState.connectionNameValidation = true;
        isValid = false;
      }
      return isValid;
    },
    async getConfigurationInfoBySubscriptionId() {
      this[CURRENT_API_STATUS]('idle');

      if (this.type === 'edit') {
        const { data } =
          await CloudService.getConfigurationInfoBySubscriptionId(
            this.initialValue.subscriptionId,
          );

        this.userInputs = {
          connectionName: data.connectionName,
          accessKey: data.accessKeyId,
          accessSecret: data.secretAccessKey,
          pullInterval: data.pullFrequencyMins,
          subscriptionId: data.subscriptionId,
        };

        this.initialState = { ...this.userInputs };
      }
    },
  },
  computed: {
    ...mapState('cloudState/connections', ['apiCallStatus', 'errorMessage']),
    getHeaderClassName() {
      if (this.apiCallStatus === 'idle' || this.apiCallStatus === 'success')
        return 'modal-heading';
      else return 'failed-header';
    },
    getHeaderTitle() {
      if (this.apiCallStatus === 'idle')
        return this.$t('aws.createNewConfigguration.createConnection');
      else if (this.apiCallStatus === 'success') return this.$t('aws.create');
      else return this.$t('aws.verificationFailed');
    },
  },
  watch: {
    'userInputs.pullInterval': function (currentValue) {
      if (parseInt(currentValue) < 1) return (this.userInputs.pullInterval = 1);
    },
    initialValue(currentValue) {
      this.userInputs = {
        ...this.userInputs,
        connectionName: currentValue.item.connectionName,
      };
    },
    apiCallStatus(currentValue, previousValue) {
      if (previousValue === 'success' && currentValue === 'idle') {
        this.onWindowHide();
      }
      if (previousValue === 'loading' && currentValue === 'idle') {
        notificationError(
          this.errorMessage,
          this.$t('aws.createNewConfigguration.errorState.couldNotConnect'),
          5,
          'bottomRight',
        );
      }
    },
  },
  data() {
    return {
      initialState: {
        connectionName: '',
        accessKey: '',
        accessSecret: '',
        pullInterval: '',
        subscriptionId: '',
      },
      userInputs: {
        connectionName: '',
        accessKey: '',
        accessSecret: '',
        pullInterval: '',
        subscriptionId: '',
      },
      errorState: {
        connectionName: false,
        accessKey: false,
        accessSecret: false,
        pullInterval: false,
        connectionNameValidation: false,
      },
    };
  },
  props: {
    showWindow: { type: Boolean, required: true },
    type: {
      type: String,
      required: true,
    },
    serviceName: {
      type: String,
      required: true,
    },
    initialValue: { type: Object, required: true },
  },
};
</script>

<template>
  <RexModalV3
    width="80.625rem"
    :show="showWindow"
    @onHide="onWindowHide"
    @onSubmit="handleSubmit"
    :submitBtnProps="{
      disabled: isEqual(initialState, userInputs),
    }"
    :submitText="$t('aws.createNewConfigguration.configure')"
    @onClose="onWindowHide"
    :enableFooter="apiCallStatus !== 'idle' && apiCallStatus !== 'failed'"
    :title="
      apiCallStatus === 'idle'
        ? $t('aws.createNewConfigguration.configureAws')
        : ''
    "
    :headerStyle="{ 'border-bottom': 'none' }"
    :footerStyle="{
      display: 'flex',
      width: '100%',
      'flex-direction': 'row-reverse',
      'margin-right': '1rem',
      'border-top': 'rgba(255, 255, 255, 0.3) 1px solid',
      'padding-top': '1.5rem',
    }"
  >
    <template #header>
      <div></div>
    </template>
    <template #body>
      <!-- default form component starts -->
      <div v-if="apiCallStatus === 'idle'">
        <div class="input-container">
          <RexModalLabel
            :primaryLabel="$t('aws.createNewConfigguration.connectionName')"
          />
          <div>
            <RexFormInput
              style="border-color: red"
              :placeholder="
                $t('aws.createNewConfigguration.enterConnectionName')
              "
              v-model="userInputs.connectionName"
              type="text"
              id="input-connection-name"
            />
            <RexInfoLabel
              :isError="true"
              v-if="
                userInputs.connectionName && errorState.connectionNameValidation
              "
              :info="
                $t(
                  'aws.createNewConfigguration.errorState.connectionVerification',
                )
              "
            />
            <RexInfoLabel
              :isError="true"
              v-if="!userInputs.connectionName && errorState.connectionName"
              :info="
                $t('aws.createNewConfigguration.errorState.connectionName')
              "
            />
          </div>
        </div>
        <div class="input-container">
          <RexModalLabel
            :primaryLabel="$t('aws.createNewConfigguration.accessKeyId')"
          />
          <div>
            <RexFormInput
              :placeholder="$t('aws.createNewConfigguration.enterAccessKeyId')"
              v-model="userInputs.accessKey"
              type="text"
              id="input-access-key"
            />
            <RexInfoLabel
              :isError="true"
              v-if="errorState.accessKey"
              :info="$t('aws.createNewConfigguration.errorState.accessKeyId')"
            />
          </div>
        </div>

        <div class="input-container">
          <RexModalLabel
            :primaryLabel="$t('aws.createNewConfigguration.secretAccessKey')"
          />
          <div>
            <RexFormInput
              :placeholder="
                $t('aws.createNewConfigguration.enterSecretAccessKey')
              "
              v-model="userInputs.accessSecret"
              type="password"
              id="input-secret-key"
            />
            <RexInfoLabel
              :isError="true"
              v-if="errorState.accessSecret"
              :info="
                $t('aws.createNewConfigguration.errorState.secretAccessKey')
              "
            />
          </div>
        </div>

        <div class="input-container">
          <RexModalLabel
            :primaryLabel="$t('aws.createNewConfigguration.pullFrequency')"
          />
          <div>
            <RexFormInput
              :placeholder="
                $t('aws.createNewConfigguration.pullFrequencyPlaceholder')
              "
              v-model="userInputs.pullInterval"
              type="text"
              id="input-pull-interval"
            />
            <RexInfoLabel
              :isError="true"
              v-if="
                errorState.pullInterval ||
                typeof errorState.pullInterval === 'string'
              "
              :info="
                $t(
                  typeof errorState.pullInterval === 'string'
                    ? 'aws.createNewConfigguration.errorState.inValidPullFrequency'
                    : 'aws.createNewConfigguration.errorState.emptyPullFrequency',
                )
              "
            />
          </div>
        </div>
      </div>
      <!-- default form component ends -->

      <!-- loading state starts -->
      <div class="loading-state" v-if="apiCallStatus === 'loading'">
        <div class="py-4">
          <RexLoading />
        </div>
        <p class="verify-text">
          {{ $t('aws.verifyingConnection') }}
        </p>
        <p>
          {{ $t('aws.verifyingConnectionDescription') }}
        </p>
      </div>
      <!-- loading state ends -->

      <!-- failed state starts -->
      <div class="failed-text" v-if="apiCallStatus === 'failed'">
        {{ $t('aws.verifyingConnectionFailedDescription') }}
      </div>
      <!-- failed state ends -->

      <!-- success state starts -->
      <div class="success-state" v-if="apiCallStatus === 'success'">
        <BaseIcon
          name="good"
          class-name="verified-icon"
          height="48px"
          width="48px"
        />
        <p class="verified-text">
          {{ $t('aws.connectionVerified') }}
        </p>
      </div>
      <!-- success state ends -->
    </template>
    <template #footer>
      <div
        v-if="apiCallStatus === 'loading' || apiCallStatus === 'success'"
      ></div>

      <div v-if="apiCallStatus === 'failed'">
        <RexButton
          @onClick="resetCurrentApiStatus"
          id="hide-aws-integration-window"
          :styling="{
            'font-size': '1.2rem',
            'border-width': 0,
          }"
          variant="transparent"
        >
          {{ $t('aws.createNewConfigguration.back') }}
        </RexButton>
        <RexButton
          id="cloud-verify-connectio-btn"
          :styling="{
            width: 'fit-content',
            'margin-left': '1rem',
            'font-size': '1.2rem',
            'background-color': 'red',
            color: 'white',
          }"
          variant="primary"
        >
          {{ $t('aws.createNewConfigguration.tryAgain') }}
        </RexButton>
      </div>
    </template>
  </RexModalV3>
</template>

<style lang="scss" scoped>
.modal-heading {
  color: white;
}
.verify-text {
  font-size: large;
  text-align: center;
}
.verified-icon {
  transform: scale(5);
  margin-top: 2rem;
  margin-bottom: 3rem;
}
.verified-text {
  font-weight: bold;
  font-size: large;
}
.failed-text,
.failed-header {
  font-weight: bold;
}
.failed-header {
  color: white;
  margin: 0px;
}
.input-container {
  display: flex;
  flex-direction: column;
  margin-top: 15px;
}
.input-secret-key {
  border: none !important;
  background: inherit !important;
}
.loading-state {
  @include flex-center-column;
}
.success-state {
  @include flex-center-column;
}
</style>
